import clsx from 'clsx';
import { useState } from 'react';

import {
  IconTrash,
  IconAlertCircle,
  IconDeviceGamepad2,
} from '@tabler/icons-react';

import ConfirmationModal from '@/components/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import SectionLoader from '@/components/Loader/SectionLoader';
import ProgressBar from '@/components/ProgressBar';
import { SummaryCard } from '@/components/SummaryCard';
import TagCapsule from '@/components/TagCapsule';
import { LessonStatusConfig } from '@/constants/constant';
import { ELessonStatus } from '@/enum';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useFormatText } from '@/hooks/useFormatText';
import {
  ITeacherLessonDetails,
  ITeacherLessonBreakdownItem,
} from '@/types/lesson';
import { formatSecondsToDuration } from '@/utils';

interface ITeacherLessonDetailsContentProps {
  lessonDetails: ITeacherLessonDetails | null;
  lessonBreakdown: ITeacherLessonBreakdownItem[];
  isBreakdownLoading?: boolean;
  onDeleteGame?: (gameId: string) => void;
}

const TeacherLessonDetailsContent = ({
  lessonDetails,
  lessonBreakdown,
  isBreakdownLoading,
  onDeleteGame,
}: ITeacherLessonDetailsContentProps) => {
  const { formatText } = useFormatText();
  const { formatDate } = useDateFormatter();

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const gameToDelete = lessonBreakdown?.find((g) => g.id === confirmDeleteId);

  const handleConfirmDelete = () => {
    if (confirmDeleteId) {
      onDeleteGame?.(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  const getBreakdownStatus = (
    completed: number,
    total: number
  ): ELessonStatus => {
    if (completed === total && total > 0) return ELessonStatus.COMPLETED;
    if (completed > 0) return ELessonStatus.IN_PROGRESS;
    return ELessonStatus.IN_PROGRESS;
  };

  return (
    <div className="flex flex-col gap-10 w-full">
      {/* Overview */}
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-neutral-primary">
          {formatText('lessons.details-drawer.overview')}
        </h3>
        <div className="bg-brand-light-50 rounded-2xl p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="bg-brand-default p-2 rounded-lg shrink-0">
              <IconDeviceGamepad2 size={24} className="text-white" />
            </div>
            <div className="flex items-center justify-between flex-1 gap-2 overflow-hidden">
              <span className="text-lg font-semibold text-neutral-primary truncate">
                {lessonDetails?.title ?? '-'}
              </span>
              <TagCapsule
                value={formatText(
                  LessonStatusConfig[
                    lessonDetails?.status ?? ELessonStatus.IN_PROGRESS
                  ].label
                )}
                className={clsx(
                  'whitespace-nowrap shrink-0',
                  LessonStatusConfig[
                    lessonDetails?.status ?? ELessonStatus.IN_PROGRESS
                  ].className
                )}
              />
            </div>
          </div>
          {/* Description */}
          {lessonDetails?.description && (
            <p className="text-neutral-secondary">
              {lessonDetails?.description}
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-base text-neutral-secondary">
              {formatText('lessons.details-drawer.assigned-date')}
            </span>
            <span className="text-base font-semibold text-neutral-primary">
              {lessonDetails?.assign_date
                ? formatDate(lessonDetails?.assign_date)
                : '-'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-base text-neutral-secondary">
              {formatText('lessons.details-drawer.due-date')}
            </span>
            <span className="text-base font-semibold text-neutral-primary">
              {lessonDetails?.due_date
                ? formatDate(lessonDetails?.due_date)
                : '-'}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-base text-neutral-secondary">
                {formatText('lessons.details-drawer.progress')}
              </span>
              <span className="text-base font-semibold text-neutral-primary">
                {lessonDetails?.progress ?? 0}%
              </span>
            </div>
            <ProgressBar
              value={lessonDetails?.progress ?? 0}
              showPercentage={false}
              height="h-2"
            />
          </div>
        </div>

        {/* Instructions */}
        {lessonDetails?.instructions && (
          <div className="rounded-2xl bg-yellow-50 p-6 space-y-2">
            <div className="flex items-center gap-2">
              <IconAlertCircle
                size={32}
                className="text-white p-2 rounded-lg bg-yellow-500 shrink-0"
              />
              <h4 className="font-semibold text-neutral-primary">
                {formatText('lessons.details-drawer.instructions')}
              </h4>
            </div>
            <p className="text-sm text-neutral-secondary">
              {lessonDetails?.instructions}
            </p>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-3 gap-4">
          <SummaryCard
            label={formatText('lessons.details-drawer.time')}
            value={formatSecondsToDuration(lessonDetails?.avg_time ?? 0)}
            bg="bg-success-light-50"
            text="text-green-900"
            sub="text-success-secondary"
            variant="highlight"
          />
          <SummaryCard
            label={formatText('lessons.details-drawer.accuracy')}
            value={`${lessonDetails?.accuracy ?? 0}%`}
            bg="bg-indigo-100"
            text="text-indigo-900"
            sub="text-indigo-600"
            variant="highlight"
          />
          <SummaryCard
            label={formatText('lessons.details-drawer.games')}
            value={lessonDetails?.games ?? 0}
            bg="bg-warning-light-100"
            text="text-warning-primary"
            sub="text-warning-secondary"
            variant="highlight"
          />
        </div>
      </div>

      {/* Lesson Breakdown */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-neutral-primary">
            {formatText('lessons.details-drawer.lesson-breakdown')}
          </h3>
          {lessonBreakdown?.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-neutral-light-100 text-xs font-semibold">
              {String(lessonBreakdown?.length).padStart(2, '0')}
            </span>
          )}
        </div>
        {isBreakdownLoading && (
          <SectionLoader className="flex items-center justify-center py-10" />
        )}
        {!isBreakdownLoading && lessonBreakdown.length > 0 && (
          <div className="flex flex-col gap-4">
            {lessonBreakdown?.map((activity) => {
              const status = getBreakdownStatus(
                activity.students_completed,
                activity.total_students
              );

              const statusConfig = LessonStatusConfig[status];
              return (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-4 border border-neutral-200 rounded-xl"
                >
                  <div className="flex flex-col gap-0.5 max-w-[70%]">
                    <div className="flex items-center gap-2 ">
                      <span
                        className="text-base font-semibold text-neutral-primary truncate"
                        title={activity?.title}
                      >
                        {activity?.title}
                      </span>
                      {activity?.quarters !== null &&
                        activity?.quarters !== undefined && (
                          <TagCapsule
                            value={`Q${activity.quarters}`}
                            className="bg-purple-50 text-brand-default"
                          />
                        )}
                      {activity?.rounds !== null &&
                        activity?.rounds !== undefined && (
                          <TagCapsule
                            value={`R${activity.rounds}`}
                            className="bg-neutral-100 text-neutral-primary"
                          />
                        )}
                    </div>

                    <span className="text-sm text-neutral-secondary">
                      {activity.students_completed}/{activity.total_students}{' '}
                      {formatText('common.status.completed')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <TagCapsule
                      value={formatText(statusConfig.label)}
                      className={statusConfig.className}
                    />
                    <IconTrash
                      size={20}
                      strokeWidth={1.5}
                      className="text-red-500 cursor-pointer shrink-0"
                      onClick={() => setConfirmDeleteId(activity.id)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!isBreakdownLoading && lessonBreakdown.length === 0 && (
          <EmptyState
            imageSrc="/assets/images/lesson-breakdown-empty.png"
            title={formatText('common.no-data-found')}
            className="h-65!"
            titleClassName="text-lg"
            width={120}
            height={120}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={!!confirmDeleteId}
        setIsOpen={(open) => {
          if (!open) setConfirmDeleteId(null);
        }}
        title={formatText('lessons.actions.remove-breakdown-title')}
        descriptionPrefix={formatText('common.confirmation.remove-prefix')}
        descriptionSuffix={formatText(
          'lessons.actions.remove-breakdown-suffix'
        )}
        entityName={gameToDelete?.title || ''}
        confirmLabel={formatText('lessons.actions.remove-breakdown')}
        cancelLabel={formatText('common.actions.cancel')}
        onConfirm={handleConfirmDelete}
        isDestructive
      />
    </div>
  );
};

export default TeacherLessonDetailsContent;
